import React, { useMemo, useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { useI18N } from '../../../libraries/i18n/hook'
import {
  getUserLanguages,
  createUserLanguage,
  updateUserLanguage,
  deleteUserLanguage
} from '../../../api/users'
import type { StudentLanguageWithLabels } from '../../../api/users/types'
import type { OptionType } from '../../components/form/SelectInput'
import SelectInput from '../../components/form/SelectInput'
import Button from '../../components/form/Button'
import Loading from '../../components/layout/Loading'
import TrashIcon from '../../../assets/icons/trash.svg?react'
const SWR_KEY_LANGUAGES = '/users/languages'

const defaultData = { items: [], languages: [], levels: [] }

export const ProfileLanguagesSection: React.FC = () => {
  const { t } = useI18N()
  const [showAddForm, setShowAddForm] = useState<boolean>(false)
  const [addLanguage, setAddLanguage] = useState<string>('')
  const [addLevel, setAddLevel] = useState<number>(0)

  const { data = defaultData, isLoading, mutate } = useSWR(SWR_KEY_LANGUAGES, getUserLanguages)
  const { items, languages: languagesOptions, levels: levelsOptions } = data

  const { trigger: createLanguage, isMutating: creating } = useSWRMutation(
    SWR_KEY_LANGUAGES,
    createUserLanguage,
    { onSuccess: () => mutate() }
  )
  const { trigger: updateLanguage, isMutating: updating } = useSWRMutation(
    SWR_KEY_LANGUAGES,
    updateUserLanguage,
    { onSuccess: () => mutate() }
  )
  const { trigger: removeLanguage, isMutating: removing } = useSWRMutation(
    SWR_KEY_LANGUAGES,
    deleteUserLanguage,
    { onSuccess: () => mutate() }
  )

  const languageOptions: OptionType[] = useMemo(() =>
    languagesOptions.map(l => ({ value: l.code, label: l.name })),
    [languagesOptions]
  )
  const levelOptions: OptionType[] = useMemo(() =>
    levelsOptions.map(l => ({ value: String(l.level), label: l.label })),
    [levelsOptions]
  )

  const availableLanguageOptions = useMemo(() =>
    languageOptions.filter(opt => !items.some(l => l.code === opt.value)),
    [languageOptions, items]
  )

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addLanguage) return
    try {
      await createLanguage({ code: addLanguage, level: addLevel })
      setAddLanguage('')
      if (levelsOptions.length > 0) setAddLevel(levelsOptions[0].level)
      setShowAddForm(false)
    } catch {
      // Error handled by API / global handler
    }
  }

  const handleLevelChange = (code: string, newLevel: number) => {
    updateLanguage({ code, level: newLevel })
  }

  const handleRemove = (code: string) => {
    removeLanguage(code)
  }

  const busy = creating || updating || removing

  if (isLoading) return <Loading size='fill-container' />

  return (
    <section className='max-w-xl'>
      <ul className='space-y-3'>
        {items.map((lang) => (
          <LanguageRow
            key={lang.code}
            lang={lang}
            levelOptions={levelOptions}
            onLevelChange={handleLevelChange}
            onRemove={handleRemove}
            disabled={busy}
          />
        ))}
      </ul>
      {availableLanguageOptions.length > 0 && (
        <>
          {showAddForm ? (
            <form onSubmit={handleAdd} className='flex flex-wrap items-end gap-1 items-center mt-4'>
              <div className='w-[120px]'>
                <SelectInput
                  name='add-language'
                  options={availableLanguageOptions}
                  value={languageOptions.find(o => o.value === addLanguage)}
                  onChange={(v) => setAddLanguage(v)}
                  isMulti={false}
                  label={t('profile.languages.add')}
                  isSearchable={false}
                  size='sm'
                  autoMinWidth
                  showBorder
                />
              </div>
              <div className='flex-grow max-w-xs'>
                <SelectInput
                  name='add-level'
                  options={levelOptions}
                  value={levelOptions.find(o => o.value === String(addLevel))}
                  onChange={(v) => setAddLevel(Number(v))}
                  isMulti={false}
                  label={t('profile.languages.level.label')}
                  isSearchable={false}
                  size='sm'
                  autoMinWidth
                  showBorder
                />
              </div>
              <Button type='submit' variant='primary' size='sm' disabled={busy || !addLanguage} loading={creating}>
                {t('common.save')}
              </Button>
            </form>
          ) : null}
        </>
      )}
      <div className='flex gap-x-4 items-center justify-center'>
        {!showAddForm && items.length === 0 ? (
          <p className='text-gray text-sm'>{t('profile.languages.empty')}</p>
        ) : <div />}
        {!showAddForm && (
          <div className={items.length === 0 ? '' : 'mt-4'}>
            <Button type='button' variant='primary' outlined disabled={showAddForm} size='sm' onClick={() => setShowAddForm(true)}>
              {t('common.add')}
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

interface LanguageRowProps {
  lang: StudentLanguageWithLabels
  levelOptions: OptionType[]
  onLevelChange: (code: string, level: number) => void
  onRemove: (code: string) => void
  disabled: boolean
}

const LanguageRow: React.FC<LanguageRowProps> = ({
  lang,
  levelOptions,
  onLevelChange,
  onRemove,
  disabled,
}) => {
  const currentLevelOption = levelOptions.find(o => o.value === String(lang.level))
  const { t } = useI18N()
  return (
    <li className='flex items-center gap-1 border-b border-gray-200 last:border-0'>
      <span className='font-bold text-left min-w-[100px]'>{lang.languageName}</span>
      <div className='flex-grow'>
        <SelectInput
          name={`level-${lang.code}`}
          options={levelOptions}
          value={currentLevelOption}
          onChange={(v) => onLevelChange(lang.code, Number(v))}
          isMulti={false}
          label={t('profile.languages.level.label')}
          isSearchable={false}
          isDisabled={disabled}
          size='sm'
          showBorder
        />
      </div>
      <Button
        type='button'
        size='sm'
        variant='flat'
        onClick={() => onRemove(lang.code)}
        disabled={disabled}
        icon={<TrashIcon className='stroke-deep-green' />}
      />
    </li>
  )
}

export default ProfileLanguagesSection
